import { useEffect } from 'react';
import { Modal, Form, Input, Spin, Image } from 'antd-v5';
import BlackButton from '../../components/BlackButton';
import PictureUpload from '../../components/PictureUpload';
import styles from './index.module.scss';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';
import EcoLink from 'eco-design/base/eco-link';

interface VerifySnModalProps {
    open: boolean;
    locale: string;
    onCancel?: () => void;
    onSuccess?: (sn: string, snUrl: string, deviceUrl: string) => void;
    loading?: boolean;
    error?: string | null;
}

interface FormValues {
    sn: string;
    snUrl: string | null;
    deviceUrl: string | null;
}

const QuestionIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
            d="M8.00003 1.8667C9.69354 1.8667 11.2269 2.55329 12.3368 3.66324C13.4468 4.7732 14.1334 6.30652 14.1334 8.00003C14.1334 9.69354 13.4468 11.2269 12.3368 12.3368C11.2269 13.4468 9.69354 14.1334 8.00003 14.1334C6.30652 14.1334 4.7732 13.4468 3.66324 12.3368C2.55329 11.2269 1.8667 9.69354 1.8667 8.00003C1.8667 6.30652 2.55329 4.7732 3.66324 3.66324C4.7732 2.55329 6.30652 1.8667 8.00003 1.8667Z"
            stroke="#18191B"
            stroke-width="1.06667"
        />
        <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8.09599 10.2639C7.70809 10.2639 7.39367 10.578 7.39367 10.9654C7.39367 11.3528 7.70809 11.6668 8.09599 11.6668C8.48388 11.6668 8.79831 11.3528 8.79831 10.9654C8.79831 10.578 8.48388 10.2639 8.09599 10.2639ZM8.0715 4.3335C7.6447 4.3335 7.26514 4.42336 6.93334 4.60376C6.60016 4.78493 6.33951 5.02904 6.15251 5.33627C5.99704 5.59486 5.90331 5.876 5.87186 6.17627L5.86133 6.33517C5.86133 6.42383 5.88847 6.49994 5.94215 6.55622C5.98082 6.59677 6.02792 6.62159 6.08185 6.63146L6.138 6.63638H6.56568C6.64419 6.63638 6.71149 6.6136 6.76526 6.56589C6.80634 6.52943 6.83708 6.4806 6.858 6.42115L6.87567 6.35822L6.90108 6.22896C6.96755 5.93792 7.08424 5.7206 7.2513 5.57047C7.44372 5.39752 7.70743 5.30963 8.04474 5.30963C8.3335 5.30963 8.58296 5.38922 8.79591 5.54785C9.0043 5.69914 9.10459 5.89618 9.10459 6.14831C9.10459 6.31982 9.05733 6.47677 8.96139 6.62218C8.90219 6.71182 8.82251 6.81616 8.72247 6.935L8.55713 7.1241L8.46302 7.22676C8.17422 7.53591 7.95392 7.81122 7.80187 8.05358C7.66599 8.27016 7.58384 8.52282 7.55527 8.80796L7.54545 8.95322L7.54533 9.33439C7.54533 9.41762 7.57308 9.49039 7.62617 9.54607C7.66446 9.58622 7.70981 9.61125 7.76058 9.62126L7.8131 9.62626H8.29426C8.37097 9.62626 8.43714 9.60175 8.48976 9.55096C8.52876 9.51333 8.55808 9.46343 8.57824 9.40265L8.59536 9.33829L8.59766 9.33694L8.59836 9.25994L8.60397 9.11663L8.61524 8.9309C8.63176 8.73764 8.69131 8.56563 8.79484 8.41127C8.88627 8.27 9.01708 8.1019 9.18715 7.90736L9.29391 7.78746C9.57074 7.47824 9.77923 7.20575 9.91953 6.96917C10.066 6.72198 10.1391 6.42878 10.1391 6.09225C10.1391 5.78685 10.063 5.50075 9.91138 5.23537C9.75976 4.96415 9.52517 4.74761 9.20795 4.58417C8.89982 4.41661 8.52091 4.3335 8.0715 4.3335Z"
            fill="#18191B"
        />
    </svg>
);

const VerifySnModal: React.FC<VerifySnModalProps> = ({ open, locale, onCancel, onSuccess, loading, error }) => {
    const { t } = useLang('VerifySnModal');
    const [form] = Form.useForm<FormValues>();

    useEffect(() => {
        if (!open) {
            form.resetFields();
        }
    }, [open, form]);

    const handleFinish = (values: FormValues) => {
        if (!values.snUrl || !values.deviceUrl) return;
        onSuccess?.(values.sn.trim(), values.snUrl, values.deviceUrl);

        gtm.push({
            event: 'uaEvent',
            event_name: 'pre_order_submit',
            form_type: 'Service Renewal',
            form_info: 'Verify SN',
            button_name: t('confirm'),
            status: 'success',
        });
    };

    return (
        <Modal className={styles.VerifySnModal} open={open} onCancel={onCancel} footer={null} centered maskClosable={false} title="Verify SN" destroyOnClose>
            <Spin spinning={loading}>
                <Form form={form} onFinish={handleFinish} layout="vertical" requiredMark={false}>
                    <Form.Item name="sn" label={t('sn_label')} className={styles.snFormItem} rules={[{ required: true, message: t('error_sn') }]}>
                        <Input placeholder="SN" />
                    </Form.Item>
                    <EcoLink className={styles.findSnDesc} href={`https://www.ecoflow.com/${locale}/support/how-to-find-sn/product-category`} target="_blank">
                        <QuestionIcon />
                        {t('find_sn_desc')}
                    </EcoLink>
                    <div className={styles.uploadRow}>
                        <div className={styles.uploadCol}>
                            <Form.Item name="snUrl" label={t('sn_photo')} rules={[{ required: true, message: t('error_sn_photo') }]}>
                                <PictureUpload />
                            </Form.Item>
                            <div className={styles.exampleImg}>
                                <Image
                                    src="https://websiteoss.ecoflow.com/figma-uploads/1784771919766-j6qmai-compressed-Frame%202094655872.jpg"
                                    alt="SN Example"
                                    width={96}
                                    height={64}
                                    preview={{ mask: false }}
                                />
                                <span className={styles.exampleTag}>{t('example')}</span>
                            </div>
                        </div>

                        <div className={styles.uploadCol}>
                            <Form.Item name="deviceUrl" label={t('full_device_photo')} rules={[{ required: true, message: t('error_full_device_photo') }]}>
                                <PictureUpload />
                            </Form.Item>
                            <div className={styles.exampleImg}>
                                <Image
                                    src="https://websiteoss.ecoflow.com/figma-uploads/1784712024843-ocnotz-compressed-Clipboard_Screenshot_1784707421%201.jpg"
                                    alt="Device Example"
                                    width={96}
                                    height={64}
                                    preview={{ mask: false }}
                                />
                                <span className={styles.exampleTag}>{t('example')}</span>
                            </div>
                        </div>
                    </div>

                    {error && <p className={styles.modalError}>*{error}</p>}

                    <div className={styles.modalFooter}>
                        <BlackButton type="primary" shape="round" htmlType="submit" block disabled={loading}>
                            {t('confirm')}
                        </BlackButton>
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
};

export default VerifySnModal;
